import { http } from '../lib/api/http';
import { uploadFileWithPresign } from './uploadsApi';
import { normalizeAvailabilityRanges, parseAvailabilityRanges } from '../utils/availabilityRanges';

const COLLECTION = 'units';
const DEFAULT_UNIT_CURRENCY = 'MXN';
const UNIT_PHOTO_UPLOAD_KIND = 'unit';

const normalizeAttributes = value => {
  if (!Array.isArray(value)) return [];
  return value
    .map(item => ({
      name: String(item?.name ?? '').trim(),
      value: String(item?.value ?? '').trim(),
    }))
    .filter(item => item.name || item.value);
};

const normalizeCustomEquipment = value => {
  if (!Array.isArray(value)) return [];
  return Array.from(
    new Set(
      value
        .map(item => String(item ?? '').trim())
        .filter(Boolean)
    )
  );
};

const parseDestinationPrice = raw => {
  if (typeof raw === 'number') {
    return Number.isFinite(raw) ? raw : null;
  }
  if (typeof raw === 'string') {
    const cleaned = raw.replace(/[^0-9.-]/g, '');
    if (!cleaned) return null;
    const numeric = Number(cleaned);
    return Number.isFinite(numeric) ? numeric : null;
  }
  return null;
};

const normalizeDestinations = value => {
  if (!Array.isArray(value)) {
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          return normalizeDestinations(parsed);
        }
      } catch {
        const label = value.trim();
        if (!label) return [];
        return [
          {
            id: label,
            destinationCode: '',
            destinationLabel: label,
            label,
            price: null,
            priceLabel: '',
          },
        ];
      }
    }
    return [];
  }
  return value
    .map(item => {
      if (typeof item === 'string') {
        const label = item.trim();
        if (!label) return null;
        return {
          id: label,
          destinationCode: '',
          destinationLabel: label,
          label,
          price: null,
          priceLabel: '',
        };
      }
      if (!item || typeof item !== 'object') return null;
      const destinationCodeRaw =
        item.destinationCode ??
        item.code ??
        item.destination ??
        item.location ??
        '';
      const destinationLabelRaw =
        item.destinationLabel ??
        item.label ??
        item.destination ??
        item.location ??
        '';
      const destinationCode = String(destinationCodeRaw || '').trim();
      const destinationLabel = String(destinationLabelRaw || '').trim();
      if (!destinationCode && !destinationLabel) {
        return null;
      }
      const fallbackId =
        item.id ||
        item.uid ||
        item.code ||
        destinationLabel ||
        destinationCode ||
        Math.random().toString(36).slice(2, 10);
      const price =
        parseDestinationPrice(item.price) ??
        parseDestinationPrice(item.amount);
      const priceLabel =
        typeof item.priceLabel === 'string' ? item.priceLabel.trim() : '';
      return {
        id: fallbackId,
        destinationCode,
        destinationLabel,
        label: destinationLabel || destinationCode,
        price,
        priceLabel,
      };
    })
    .filter(Boolean);
};

const parsePricingField = raw => {
  if (!raw) return {};
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch (err) {
      return {};
    }
  }
  if (typeof raw === 'object') return raw;
  return {};
};

const formatPriceLabel = (currency, amount) => {
  if (amount === null || typeof amount === 'undefined') return '';
  return `${currency} ${Number(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export const mapUnitRecord = record => {
  if (!record) return null;

  const parsedPricing = parsePricingField(
    record.pricing || record.pricing_json || record.pricing_jsonb
  );
  const pricingCurrency = parsedPricing.currency || record.currency || DEFAULT_UNIT_CURRENCY;
  const basePrice =
    typeof parsedPricing.base === 'number'
      ? parsedPricing.base
      : typeof record.unitPrice === 'number'
      ? record.unitPrice
      : typeof record.unit_price === 'number'
      ? record.unit_price
      : null;

  const destinationsRaw =
    parsedPricing.destinations || record.destinationPrices || record.destination_prices;
  const destinations = normalizeDestinations(destinationsRaw).map(entry => {
    const resolvedPriceLabel =
      entry.priceLabel ||
      (entry.price !== null && entry.price !== undefined
        ? formatPriceLabel(pricingCurrency, entry.price)
        : '');
    return {
      ...entry,
      priceLabel: resolvedPriceLabel,
      currency: pricingCurrency,
    };
  });
  const priceLabel =
    record.priceLabel ||
    record.price_label ||
    (basePrice !== null ? formatPriceLabel(pricingCurrency, basePrice) : '');

  const photoUrl = record.photo_url || record.photoUrl || '';
  const photoKey = record.photo_key || record.photoKey || record.photo || '';

  return {
    id: record.id,
    tractorType: record.tractor_type || record.tractorType || '',
    model: record.model || '',
    capacity: record.capacity || '',
    economicNumber: record.economicNumber || record.economic_number || '',
    status: record.status || 'available',
    currency: pricingCurrency,
    unitPrice: basePrice,
    priceLabel,
    pricing: {
      currency: pricingCurrency,
      base: basePrice,
      destinations,
    },
    destinationPrices: destinations,
    sla: record.sla || '',
    liftGate: Boolean(record.liftGate ?? record.lift_gate),
    gps: Boolean(record.gps),
    insured: Boolean(record.insured),
    cTpat: Boolean(record.cTpat ?? record.ctpat),
    eTracking: Boolean(record.eTracking ?? record.e_tracking),
    armored: Boolean(record.armored),
    teamDrivers: Boolean(record.teamDrivers ?? record.team_drivers),
    hazmat: Boolean(record.hazmat),
    customEquipment: normalizeCustomEquipment(record.customEquipment || record.custom_equipment),
    extraAttributes: normalizeAttributes(record.extraAttributes || record.extra_attributes),
    photo: photoKey,
    photoKey,
    photoUrl,
    company: record.company || record.company_id || '',
    createdBy: record.createdBy || record.created_by || '',
    created: record.created_at || record.created,
    updated: record.updated_at || record.updated,
    availabilityRanges: parseAvailabilityRanges(
      record.availability_ranges || record.availabilityRanges || record.availability
    ),
  };
};

const normalizeUnitPriceValue = value => {
  if (value === null || typeof value === 'undefined' || value === '') {
    return null;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const resolvePricingPayload = (payload = {}) => {
  const pricingCurrency = payload.currency || DEFAULT_UNIT_CURRENCY;
  return {
    currency: pricingCurrency,
    base: normalizeUnitPriceValue(payload.base ?? payload.unitPrice),
    destinations: normalizeDestinations(payload.destinations),
  };
};

const resolveDestinationPrices = values =>
  normalizeDestinations(values).map(entry => ({
    destination_code: entry.destinationCode,
    destination_label: entry.destinationLabel,
    label: entry.label,
    id: entry.id,
    price: entry.price,
    priceLabel: entry.priceLabel,
  }));

const attachUnitPhoto = async (payload, { photoFile, clearPhoto, photoKey, photoUrl }) => {
  if (photoFile instanceof File) {
    const presign = await uploadFileWithPresign({ kind: UNIT_PHOTO_UPLOAD_KIND, file: photoFile });
    payload.photo_key = presign?.object_key || '';
    payload.photo_url = presign?.public_url || '';
    return;
  }
  if (clearPhoto) {
    payload.photo_key = '';
    payload.photo_url = '';
    return;
  }
  if (photoUrl) {
    payload.photo_url = photoUrl;
  }
  if (photoKey) {
    payload.photo_key = photoKey;
  }
};

const buildUnitPayload = async (values = {}, { keepExistingPhoto = false } = {}) => {
  const payload = {
    tractor_type: values.tractorType ?? '',
    model: values.model ?? '',
    capacity: values.capacity ?? '',
    economic_number: values.economicNumber ?? '',
    status: values.status || 'available',
    currency: values.currency || DEFAULT_UNIT_CURRENCY,
    unit_price: normalizeUnitPriceValue(values.unitPrice),
    price_label: values.priceLabel || '',
    lift_gate: Boolean(values.liftGate),
    gps: Boolean(values.gps),
    insured: Boolean(values.insured),
    ctpat: Boolean(values.cTpat),
    e_tracking: Boolean(values.eTracking),
    armored: Boolean(values.armored),
    team_drivers: Boolean(values.teamDrivers),
    hazmat: Boolean(values.hazmat),
    custom_equipment: normalizeCustomEquipment(values.customEquipment),
    extra_attributes: normalizeAttributes(values.extraAttributes),
    availability_ranges: normalizeAvailabilityRanges(values.availabilityRanges),
  };

  if (values.pricing || typeof values.unitPrice !== 'undefined') {
    payload.pricing = resolvePricingPayload({
      currency: values.pricing?.currency || values.currency,
      base: values.pricing?.base ?? values.unitPrice,
      destinations: values.pricing?.destinations,
      unitPrice: values.unitPrice,
    });
  }

  if (Array.isArray(values.destinationPrices)) {
    payload.destination_prices = resolveDestinationPrices(values.destinationPrices);
  }

  if (values.companyId || values.company_id || values.company) {
    payload.company_id = values.companyId || values.company_id || values.company;
  }

  if (values.createdBy || values.created_by) {
    payload.created_by = values.createdBy || values.created_by;
  }

  await attachUnitPhoto(payload, {
    photoFile: values.photoFile,
    clearPhoto: values.clearPhoto,
    photoKey: keepExistingPhoto ? values.photoKey || values.photo : '',
    photoUrl: keepExistingPhoto ? values.photoUrl : '',
  });

  return payload;
};

export const UNIT_TYPES = [
  { value: 'container_single', label: 'Portacontenedor sencillo' },
  { value: 'container_full', label: 'Portacontenedor full' },
  { value: '3_5_ton', label: '3.5 Ton' },
  { value: 'rabon', label: 'Rabon' },
  { value: 'dry_box_53', label: "Caja seca 53'" },
  { value: 'dry_box_48', label: "Caja de 48'" },
  { value: 'dry_box_40', label: "Caja seca de 40'" },
  { value: 'refrigerated_box', label: 'Caja refrigerada' },
  { value: 'sprinter_van', label: 'Sprinter van' },
  { value: 'lowboy', label: 'Lowboy' },
  { value: 'flatbed_48', label: "Flatbed 48'" },
  { value: 'flatbed_53', label: 'Flatbed 53' },
  { value: 'flatbed_40', label: "Flatbed 40'" },
  { value: 'torton', label: 'Torton' },
  { value: '1_ton', label: '1 Ton' },
];

export const UNIT_STATUS_OPTIONS = [
  { value: 'available', label: 'Disponible' },
  { value: 'reserved', label: 'Reservada' },
  { value: 'maintenance', label: 'Mantenimiento' },
  { value: 'unavailable', label: 'No disponible' },
];

export async function listUnits({ companyId, createdBy } = {}) {
  const params = {};
  if (companyId) {
    params.company_id = companyId;
  }
  if (createdBy) {
    params.created_by = createdBy;
  }
  const response = await http.get(`/${COLLECTION}`, { params });
  const items = Array.isArray(response.data) ? response.data : [];
  return items.map(mapUnitRecord).filter(Boolean);
}

export async function getUnitById(id) {
  if (!id) {
    throw new Error('El identificador de la unidad es requerido.');
  }
  const response = await http.get(`/${COLLECTION}/${encodeURIComponent(id)}`);
  return mapUnitRecord(response.data);
}

export async function createUnit(payload) {
  const body = await buildUnitPayload(payload);
  const response = await http.post(`/${COLLECTION}`, body);
  return mapUnitRecord(response.data);
}

export async function updateUnit(id, payload) {
  if (!id) {
    throw new Error('El identificador de la unidad es requerido.');
  }
  const body = await buildUnitPayload(payload, { keepExistingPhoto: true });
  const response = await http.patch(`/${COLLECTION}/${encodeURIComponent(id)}`, body);
  return mapUnitRecord(response.data);
}

export async function deleteUnit(id) {
  if (!id) return false;
  await http.delete(`/${COLLECTION}/${encodeURIComponent(id)}`);
  return true;
}

export function getUnitPhotoUrl(unit) {
  if (!unit) return '';
  return unit.photoUrl || unit.photo_url || '';
}
